"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import type { Route } from "next"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Menu, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePathname } from "next/navigation"
import { getSiteInfoClient, getLogoImageClient } from "@/lib/site-info-client"

interface NavbarProps {
  logoUrl?: string | null
  corPrimaria?: string
  corSecundaria?: string
}

export default function Navbar({ logoUrl: initialLogoUrl, corPrimaria: initialCorPrimaria, corSecundaria: initialCorSecundaria }: NavbarProps) {
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl)
  const [corPrimaria, setCorPrimaria] = useState(initialCorPrimaria || "#e4a99f")
  const [corSecundaria, setCorSecundaria] = useState(initialCorSecundaria || "#21365b")
  const [nomeSite, setNomeSite] = useState("")
  
  useEffect(() => {
    if (!initialLogoUrl || !initialCorPrimaria) {
      async function loadData() {
        const [siteInfo, logoData] = await Promise.all([
          getSiteInfoClient(),
          getLogoImageClient()
        ])
        setCorPrimaria(siteInfo.corPrimaria)
        setCorSecundaria(siteInfo.corSecundaria)
        setLogoUrl(logoData?.url || null)
        const nomes = siteInfo.nomeNoiva && siteInfo.nomeNoivo 
          ? `${siteInfo.nomeNoiva} e ${siteInfo.nomeNoivo}` 
          : siteInfo.nomeSite || "Noivos"
        setNomeSite(nomes)
      }
      loadData()
    }
  }, [initialLogoUrl, initialCorPrimaria])
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const pathname = usePathname()

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
  }

  const navItems = [
    { name: "Início", href: "/" },
    { name: "Informações", href: "/informacoes" },
    { name: "Lista de Presentes", href: "/lista-de-presentes" },
    { name: "Eventos", href: "/eventos" },
  ]

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md py-2">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          <Link href="/" className="relative h-12 w-36">
            <Image src={logoUrl || "/placeholder.svg"} alt={nomeSite || "Noivos"} fill className="object-contain" priority />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <Link
                key={item.name}
                href={item.href as Route}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-primary-custom",
                  pathname === item.href ? "text-primary-custom" : "text-gray-900",
                )}
              >
                {item.name}
              </Link>
            ))}
            <Link href={"/login" as Route}>
              <Button
                variant="outline"
                className="border-primary-custom text-primary-custom hover:bg-primary-custom/10"
                style={{
                  borderColor: corPrimaria || "var(--color-primary)",
                  color: corPrimaria || "var(--color-primary)",
                }}
              >
                Área do Admin
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-900"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <nav className="flex flex-col py-4">
            {navItems.map((item) => (
              <Link
                key={item.name}
                href={item.href as Route}
                className={cn(
                  "px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-100",
                  pathname === item.href ? "text-primary-custom" : "text-gray-900",
                )}
                onClick={closeMenu}
              >
                {item.name}
              </Link>
            ))}
            <Link href={"/login" as Route} onClick={closeMenu}>
              <div className="px-4 py-2 text-sm font-medium text-primary-custom hover:bg-gray-100">Área do Admin</div>
            </Link>
          </nav>
        </div>
      )}
    </header>
  )
}
